import { useCallback, useEffect, useMemo, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { VariableSizeList as List } from 'react-window';
import { getActivityFeedSecondaryLineCount } from '../NRViewActivityFeed/NRActivityFeedItemHeader';

/** Matches ActivityFeedItem: py 1.5 (24) + content minHeight 44 */
export const ACTIVITY_FEED_BASE_ITEM_HEIGHT = 68;
/** Approx. body2 line height for each vulnerability update / extra secondary line */
export const ACTIVITY_FEED_UPDATE_LINE_HEIGHT = 20;

const HiddenScrollbarList = styled(List)({
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' }
});

export function getActivityFeedItemHeight(item) {
  const secondaryLines = getActivityFeedSecondaryLineCount(item);
  if (secondaryLines <= 1) return ACTIVITY_FEED_BASE_ITEM_HEIGHT;

  return ACTIVITY_FEED_BASE_ITEM_HEIGHT + (secondaryLines - 1) * ACTIVITY_FEED_UPDATE_LINE_HEIGHT;
}

/**
 * Variable-size virtualized list for activity feed group items,
 * where VulnerabilityUpdated rows can include multiple update lines.
 */
function NRActivityFeedVirtualizedList({ list, Component, listHeight = 600, width = '580px' }) {
  const listRef = useRef();

  const getItemSize = useCallback(index => getActivityFeedItemHeight(list[index]), [list]);

  const totalHeight = useMemo(() => {
    if (!list?.length) return 0;
    return list.reduce((sum, item) => sum + getActivityFeedItemHeight(item), 0);
  }, [list]);

  useEffect(() => {
    listRef.current?.resetAfterIndex(0, true);
  }, [list]);

  return (
    <HiddenScrollbarList ref={listRef} height={Math.min(totalHeight, listHeight)} itemCount={list?.length ?? 0} itemSize={getItemSize} width={width}>
      {Component}
    </HiddenScrollbarList>
  );
}

export default NRActivityFeedVirtualizedList;
